import React, { useEffect, useRef, useState } from "react";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { tNegocio } from "./shared";
import { useStyles } from "./styles";

const SHEET_LABELS = {
  Productos: "Productos",
  Proveedores: "Proveedores",
  M_Pago: "Métodos de pago",
  Cuentas: "Cuentas",
  Perfiles: "Perfiles",
  Cliente: "Clientes",
  Ventas: "Ventas",
  Base: "Base (suscripciones)",
  Control_Ingresos: "Control de ingresos",
  Puntos: "Puntos"
};

export default function MigrateAppSheetTab() {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const pickFile = (e) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setPreview(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const runPreview = async () => {
    if (!file) {
      toast.warn(tNegocio("pages.settings.migratePickFile"));
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/negocio/migrate/appsheet/preview", form);
      setPreview(data);
      if (!data.ready) {
        toast.warn(tNegocio("pages.settings.migrateMissingSheets"));
      } else {
        toast.success(tNegocio("pages.settings.migratePreviewOk"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  // La migración corre en segundo plano en el servidor (tarda minutos y el proxy corta a los ~100 s):
  // el POST solo la inicia y aquí se consulta el estado hasta que termina.
  const pollTimer = useRef(null);
  const mounted = useRef(true);

  const pollStatus = async () => {
    try {
      const { data } = await api.get("/negocio/migrate/appsheet/status");
      if (!mounted.current) return;
      if (data.status === "running") {
        setBusy(true);
        pollTimer.current = setTimeout(pollStatus, 3000);
        return;
      }
      setBusy(false);
      if (data.status === "done") {
        setResult(data.result);
        toast.success(tNegocio("pages.settings.migrateDone"));
      } else if (data.status === "error") {
        toast.error(data.error || tNegocio("pages.settings.migrateFailed"));
      }
    } catch (err) {
      // Error de red pasajero: seguir consultando, la migración sigue en el servidor.
      if (mounted.current) pollTimer.current = setTimeout(pollStatus, 5000);
    }
  };

  useEffect(() => {
    mounted.current = true;
    // Si se recarga la página con una migración en curso, retomar el seguimiento sin repetir avisos viejos.
    api
      .get("/negocio/migrate/appsheet/status")
      .then(({ data }) => {
        if (mounted.current && data.status === "running") {
          setBusy(true);
          pollTimer.current = setTimeout(pollStatus, 3000);
        }
      })
      .catch(() => {});
    return () => {
      mounted.current = false;
      clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runMigrate = async () => {
    if (!file) return;
    setConfirmOpen(false);
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("replaceExisting", replaceExisting ? "true" : "false");
      await api.post("/negocio/migrate/appsheet", form, { timeout: 5 * 60 * 1000 });
      toast.info(tNegocio("pages.settings.migrateStarted"));
      pollTimer.current = setTimeout(pollStatus, 3000);
    } catch (err) {
      toastError(err);
      setBusy(false);
    }
  };

  return (
    <div>
      <div className={classes.captionBlock} style={{ marginBottom: 16, maxWidth: 720 }}>
        {tNegocio("pages.settings.migrateHint")}
      </div>

      <div className={classes.captionBlock} style={{ marginBottom: 12, maxWidth: 720 }}>
        {tNegocio("pages.settings.migrateSheets")}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        onChange={pickFile}
      />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 16 }}>
        <Button variant="outlined" color="primary" onClick={() => inputRef.current?.click()} disabled={busy}>
          {file ? tNegocio("pages.settings.migrateChangeFile") : tNegocio("pages.settings.migratePickFile")}
        </Button>
        {file ? (
          <span className={classes.captionBlock} style={{ margin: 0 }}>
            {file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB
          </span>
        ) : null}
      </div>

      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={replaceExisting}
            onChange={(e) => setReplaceExisting(e.target.checked)}
            disabled={busy}
          />
        }
        label={tNegocio("pages.settings.migrateReplace")}
        style={{ display: "block", marginBottom: 8 }}
      />
      <div className={classes.captionBlock} style={{ marginBottom: 20, maxWidth: 720 }}>
        {tNegocio("pages.settings.migrateReplaceHint")}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 24 }}>
        <Button variant="outlined" color="primary" onClick={runPreview} disabled={!file || busy}>
          {busy && !result ? <CircularProgress size={18} style={{ marginRight: 8 }} /> : null}
          {tNegocio("pages.settings.migratePreview")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setConfirmOpen(true)}
          disabled={!file || busy || (preview && !preview.ready)}
        >
          {tNegocio("pages.settings.migrateRun")}
        </Button>
      </div>

      {preview ? (
        <div style={{ marginBottom: 24, maxWidth: 560 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>
            {tNegocio("pages.settings.migratePreviewTitle")}
          </div>
          {preview.missingRequired?.length ? (
            <div style={{ color: "#c62828", marginBottom: 8 }}>
              {tNegocio("pages.settings.migrateMissing")}: {preview.missingRequired.join(", ")}
            </div>
          ) : (
            <div style={{ color: "#2e7d32", marginBottom: 8 }}>
              {tNegocio("pages.settings.migrateReady")}
            </div>
          )}
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <tbody>
              {Object.entries(SHEET_LABELS).map(([key, label]) => {
                const n = preview.counts?.[key];
                if (n == null) return null;
                return (
                  <tr key={key}>
                    <td style={{ padding: "4px 8px 4px 0" }}>{label}</td>
                    <td style={{ padding: 4, textAlign: "right", fontWeight: 700 }}>{n}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {result?.counts ? (
        <div style={{ maxWidth: 560 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>
            {tNegocio("pages.settings.migrateResultTitle")}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
            <tbody>
              {[
                ["products", "Productos"],
                ["suppliers", "Proveedores"],
                ["payments", "Métodos de pago"],
                ["accounts", "Cuentas"],
                ["profiles", "Perfiles"],
                ["customers", "Clientes"],
                ["sales", "Ventas"],
                ["subscriptions", "Suscripciones"],
                ["active", "Activas"],
                ["expired", "Vencidas"],
                ["points", "Movimientos de puntos"],
                ["productsNormalized", "Productos normalizados"]
              ].map(([key, label]) => (
                <tr key={key}>
                  <td style={{ padding: "4px 8px 4px 0" }}>{label}</td>
                  <td style={{ padding: 4, textAlign: "right", fontWeight: 700 }}>
                    {result.counts[key] ?? 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.warnings?.length ? (
            <div style={{ marginTop: 12, color: "#b26a00", fontSize: 13 }}>
              {result.warnings.slice(0, 5).join(" · ")}
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmationModal
        title={tNegocio("pages.settings.migrateConfirmTitle")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={runMigrate}
      >
        {replaceExisting
          ? tNegocio("pages.settings.migrateConfirmReplace")
          : tNegocio("pages.settings.migrateConfirmKeep")}
      </ConfirmationModal>
    </div>
  );
}
